import React, { useState, useEffect } from 'react';
import {
  Box,
  Paper,
  Typography,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  Chip,
  IconButton,
  TextField,
  InputAdornment,
  LinearProgress,
  Menu,
  MenuItem,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Alert,
  Select,
  FormControl,
  InputLabel,
  Tooltip,
} from '@mui/material';
import {
  Add as AddIcon,
  Search as SearchIcon,
  Refresh as RefreshIcon,
  MoreVert as MoreIcon,
  Send as SendIcon,
  Visibility as ViewIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Pause as PauseIcon,
  PlayArrow as ResumeIcon,
  ContentCopy as DuplicateIcon,
  Assessment as StatsIcon,
  Preview as PreviewIcon,
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { marketingApi } from '../../api/marketing';
import type { MarketCampaign } from '../../api/marketing';

const CampaignsPage: React.FC = () => {
  const navigate = useNavigate();
  const [campaigns, setCampaigns] = useState<MarketCampaign[]>([]);
  const [loading, setLoading] = useState(false);
  const [totalCount, setTotalCount] = useState(0);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(25);
  const [searchQuery, setSearchQuery] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [selectedCampaign, setSelectedCampaign] = useState<MarketCampaign | null>(null);
  const [confirmDialog, setConfirmDialog] = useState<{ open: boolean; action: string }>({
    open: false,
    action: '',
  });
  const [previewDialog, setPreviewDialog] = useState(false);
  const [previewHtml, setPreviewHtml] = useState('');
  const [loadingPreview, setLoadingPreview] = useState(false);

  useEffect(() => {
    fetchCampaigns();
  }, [page, rowsPerPage, searchQuery, statusFilter]);

  const fetchCampaigns = async () => {
    try {
      setLoading(true);
      const response = await marketingApi.getCampaigns({
        page: page + 1,
        search: searchQuery || undefined,
        status: statusFilter || undefined,
      });
      setCampaigns(response.results);
      setTotalCount(response.count);
    } catch (error) {
      console.error('Failed to fetch campaigns:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleActionClick = (event: React.MouseEvent<HTMLElement>, campaign: MarketCampaign) => {
    setAnchorEl(event.currentTarget);
    setSelectedCampaign(campaign);
  };

  const handleActionClose = () => {
    setAnchorEl(null);
  };

  const handleSendCampaign = async () => {
    if (!selectedCampaign) return;

    try {
      await marketingApi.sendCampaign(selectedCampaign.id);
      await fetchCampaigns();
    } catch (error) {
      console.error('Failed to send campaign:', error);
    }
    setConfirmDialog({ open: false, action: '' });
  };

  const handlePauseCampaign = async () => {
    if (!selectedCampaign) return;

    try {
      await marketingApi.pauseCampaign(selectedCampaign.id);
      await fetchCampaigns();
    } catch (error) {
      console.error('Failed to pause campaign:', error);
    }
  };

  const handleResumeCampaign = async () => {
    if (!selectedCampaign) return;

    try {
      await marketingApi.resumeCampaign(selectedCampaign.id);
      await fetchCampaigns();
    } catch (error) {
      console.error('Failed to resume campaign:', error);
    }
  };

  const handleDeleteCampaign = async () => {
    if (!selectedCampaign) return;

    try {
      await marketingApi.deleteCampaign(selectedCampaign.id);
      await fetchCampaigns();
      setSelectedCampaign(null);
    } catch (error) {
      console.error('Failed to delete campaign:', error);
      alert('Failed to delete campaign. Please try again.');
    }
    setConfirmDialog({ open: false, action: '' });
  };

  const handlePreviewCampaign = async () => {
    if (!selectedCampaign) return;

    try {
      setLoadingPreview(true);
      setPreviewDialog(true);
      const preview = await marketingApi.previewCampaign(
        selectedCampaign.id,
        'john@example.com',
        'John Doe'
      );
      setPreviewHtml(preview.html);
    } catch (error) {
      console.error('Failed to load preview:', error);
      alert('Failed to load campaign preview');
      setPreviewDialog(false);
    } finally {
      setLoadingPreview(false);
    }
  };

  const getStatusColor = (status: string): 'default' | 'primary' | 'success' | 'warning' | 'error' => {
    const colors: Record<string, any> = {
      draft: 'default',
      scheduled: 'primary',
      queued: 'primary',
      sending: 'warning',
      paused: 'warning',
      sent: 'success',
      failed: 'error',
      cancelled: 'error',
    };
    return colors[status] || 'default';
  };

  const formatPercentage = (value: number, total: number): string => {
    if (total === 0) return '0%';
    return `${((value / total) * 100).toFixed(1)}%`;
  };

  const handleChangePage = (event: unknown, newPage: number) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event: React.ChangeEvent<HTMLInputElement>) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  return (
    <Box sx={{ flexGrow: 1, p: 3 }}>
      {/* Header */}
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
        <Typography variant="h4" component="h1">
          Campaigns
        </Typography>
        <Button
          variant="contained"
          startIcon={<AddIcon />}
          onClick={() => navigate('/marketing/campaigns/create')}
        >
          Create Campaign
        </Button>
      </Box>

      {/* Filters */}
      <Paper sx={{ p: 2, mb: 3 }}>
        <Box display="flex" gap={2} alignItems="center">
          <TextField
            placeholder="Search campaigns..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon />
                </InputAdornment>
              ),
            }}
            sx={{ flex: 1, maxWidth: 400 }}
          />
          <FormControl sx={{ minWidth: 150 }}>
            <InputLabel>Status</InputLabel>
            <Select
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value)}
              label="Status"
            >
              <MenuItem value="">All Status</MenuItem>
              <MenuItem value="draft">Draft</MenuItem>
              <MenuItem value="scheduled">Scheduled</MenuItem>
              <MenuItem value="sending">Sending</MenuItem>
              <MenuItem value="sent">Sent</MenuItem>
              <MenuItem value="paused">Paused</MenuItem>
              <MenuItem value="failed">Failed</MenuItem>
            </Select>
          </FormControl>
          <IconButton onClick={fetchCampaigns} color="primary">
            <RefreshIcon />
          </IconButton>
        </Box>
      </Paper>

      {/* Campaigns Table */}
      <Paper>
        {loading && <LinearProgress />}
        <TableContainer>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>Campaign</TableCell>
                <TableCell>Audience</TableCell>
                <TableCell>Status</TableCell>
                <TableCell align="center">Progress</TableCell>
                <TableCell align="center">Sent</TableCell>
                <TableCell align="center">Opens</TableCell>
                <TableCell align="center">Clicks</TableCell>
                <TableCell align="center">Unsubscribes</TableCell>
                <TableCell>Created</TableCell>
                <TableCell align="center">Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {campaigns.map((campaign) => (
                <TableRow key={campaign.id}>
                  <TableCell>
                    <Box>
                      <Typography
                        variant="body2"
                        fontWeight="bold"
                        sx={{ cursor: 'pointer', color: 'primary.main' }}
                        onClick={() => navigate(`/marketing/campaigns/${campaign.id}`)}
                      >
                        {campaign.name}
                      </Typography>
                      <Typography variant="caption" color="textSecondary">
                        {campaign.subject}
                      </Typography>
                    </Box>
                  </TableCell>
                  <TableCell>{campaign.audience_name}</TableCell>
                  <TableCell>
                    <Chip
                      label={campaign.status}
                      size="small"
                      color={getStatusColor(campaign.status)}
                    />
                  </TableCell>
                  <TableCell align="center">
                    <Box display="flex" alignItems="center" justifyContent="center">
                      <LinearProgress
                        variant="determinate"
                        value={campaign.progress}
                        sx={{ width: 80, mr: 1 }}
                      />
                      <Typography variant="caption">{campaign.progress}%</Typography>
                    </Box>
                  </TableCell>
                  <TableCell align="center">
                    {campaign.sent_count}/{campaign.total_recipients}
                  </TableCell>
                  <TableCell align="center">
                    {campaign.opened_count}
                    <Typography variant="caption" display="block" color="textSecondary">
                      {formatPercentage(campaign.opened_count, campaign.sent_count)}
                    </Typography>
                  </TableCell>
                  <TableCell align="center">
                    {campaign.clicked_count}
                    <Typography variant="caption" display="block" color="textSecondary">
                      {formatPercentage(campaign.clicked_count, campaign.sent_count)}
                    </Typography>
                  </TableCell>
                  <TableCell align="center">
                    {campaign.unsubscribed_count}
                    <Typography variant="caption" display="block" color="textSecondary">
                      {formatPercentage(campaign.unsubscribed_count, campaign.sent_count)}
                    </Typography>
                  </TableCell>
                  <TableCell>{new Date(campaign.created_at).toLocaleDateString()}</TableCell>
                  <TableCell align="center">
                    <IconButton
                      size="small"
                      onClick={(e) => handleActionClick(e, campaign)}
                    >
                      <MoreIcon />
                    </IconButton>
                  </TableCell>
                </TableRow>
              ))}
              {campaigns.length === 0 && !loading && (
                <TableRow>
                  <TableCell colSpan={10} align="center" sx={{ py: 4 }}>
                    <Typography color="textSecondary">
                      No campaigns found. Create your first campaign to get started.
                    </Typography>
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </TableContainer>
        <TablePagination
          component="div"
          count={totalCount}
          page={page}
          onPageChange={handleChangePage}
          rowsPerPage={rowsPerPage}
          onRowsPerPageChange={handleChangeRowsPerPage}
          rowsPerPageOptions={[25, 50, 100]}
        />
      </Paper>

      {/* Actions Menu */}
      <Menu
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={handleActionClose}
      >
        <MenuItem onClick={() => {
          navigate(`/marketing/campaigns/${selectedCampaign?.id}`);
          handleActionClose();
        }}>
          <ViewIcon sx={{ mr: 1 }} fontSize="small" />
          View Details
        </MenuItem>
        <MenuItem onClick={() => {
          handlePreviewCampaign();
          handleActionClose();
        }}>
          <PreviewIcon sx={{ mr: 1 }} fontSize="small" />
          Preview Email
        </MenuItem>
        {selectedCampaign?.can_send && (
          <MenuItem onClick={() => {
            setConfirmDialog({ open: true, action: 'send' });
            handleActionClose();
          }}>
            <SendIcon sx={{ mr: 1 }} fontSize="small" />
            Send Campaign
          </MenuItem>
        )}
        {selectedCampaign?.status === 'sending' && (
          <MenuItem onClick={() => {
            handlePauseCampaign();
            handleActionClose();
          }}>
            <PauseIcon sx={{ mr: 1 }} fontSize="small" />
            Pause Campaign
          </MenuItem>
        )}
        {selectedCampaign?.status === 'paused' && (
          <MenuItem onClick={() => {
            handleResumeCampaign();
            handleActionClose();
          }}>
            <ResumeIcon sx={{ mr: 1 }} fontSize="small" />
            Resume Campaign
          </MenuItem>
        )}
        {selectedCampaign?.status === 'draft' && (
          <MenuItem onClick={() => {
            navigate(`/marketing/campaigns/${selectedCampaign?.id}/edit`);
            handleActionClose();
          }}>
            <EditIcon sx={{ mr: 1 }} fontSize="small" />
            Edit
          </MenuItem>
        )}
        <MenuItem onClick={() => {
          navigate(`/marketing/campaigns/${selectedCampaign?.id}/stats`);
          handleActionClose();
        }}>
          <StatsIcon sx={{ mr: 1 }} fontSize="small" />
          View Stats
        </MenuItem>
        <MenuItem onClick={() => {
          // TODO: Implement duplicate
          handleActionClose();
        }}>
          <DuplicateIcon sx={{ mr: 1 }} fontSize="small" />
          Duplicate
        </MenuItem>
        {(selectedCampaign?.status === 'draft' || selectedCampaign?.status === 'failed' || selectedCampaign?.status === 'cancelled') && (
          <MenuItem
            onClick={() => {
              setConfirmDialog({ open: true, action: 'delete' });
              handleActionClose();
            }}
            sx={{ color: 'error.main' }}
          >
            <DeleteIcon sx={{ mr: 1 }} fontSize="small" />
            Delete
          </MenuItem>
        )}
      </Menu>

      {/* Confirm Dialog */}
      <Dialog
        open={confirmDialog.open}
        onClose={() => setConfirmDialog({ open: false, action: '' })}
      >
        <DialogTitle>
          {confirmDialog.action === 'send' && 'Confirm Send Campaign'}
          {confirmDialog.action === 'delete' && 'Confirm Delete Campaign'}
        </DialogTitle>
        <DialogContent>
          {confirmDialog.action === 'send' && (
            <Alert severity="warning">
              Are you sure you want to send this campaign to {selectedCampaign?.total_recipients} recipients?
              This action cannot be undone.
            </Alert>
          )}
          {confirmDialog.action === 'delete' && (
            <Alert severity="error">
              Are you sure you want to delete "{selectedCampaign?.name}"?
              This action cannot be undone.
            </Alert>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setConfirmDialog({ open: false, action: '' })}>
            Cancel
          </Button>
          {confirmDialog.action === 'send' && (
            <Button
              onClick={handleSendCampaign}
              variant="contained"
              color="primary"
            >
              Confirm Send
            </Button>
          )}
          {confirmDialog.action === 'delete' && (
            <Button
              onClick={handleDeleteCampaign}
              variant="contained"
              color="error"
            >
              Delete Campaign
            </Button>
          )}
        </DialogActions>
      </Dialog>

      {/* Preview Dialog */}
      <Dialog
        open={previewDialog}
        onClose={() => setPreviewDialog(false)}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>
          Email Preview - {selectedCampaign?.name}
        </DialogTitle>
        <DialogContent>
          {loadingPreview ? (
            <Box display="flex" justifyContent="center" alignItems="center" minHeight={300}>
              <Typography>Loading preview...</Typography>
            </Box>
          ) : (
            <Paper
              elevation={2}
              sx={{
                border: '1px solid #e0e0e0',
                borderRadius: 2,
                overflow: 'hidden',
              }}
            >
              {/* Email Header */}
              <Box
                sx={{
                  p: 2.5,
                  bgcolor: '#fafafa',
                  borderBottom: '1px solid #e0e0e0',
                }}
              >
                <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 1 }}>
                  {selectedCampaign?.subject || '(No subject)'}
                </Typography>
                <Box display="flex" alignItems="center" gap={1} flexWrap="wrap">
                  <Typography variant="body2" color="textSecondary">
                    <strong>{selectedCampaign?.from_name || 'Your Name'}</strong>
                    {' '}&lt;{selectedCampaign?.from_email || 'noreply@example.com'}&gt;
                  </Typography>
                </Box>
                {selectedCampaign?.preview_text && (
                  <Typography variant="body2" color="textSecondary" sx={{ mt: 1, fontStyle: 'italic' }}>
                    {selectedCampaign.preview_text}
                  </Typography>
                )}
              </Box>

              {/* Email Body */}
              <Box
                sx={{
                  bgcolor: '#ffffff',
                  minHeight: 450,
                  maxHeight: 700,
                  position: 'relative',
                }}
              >
                {previewHtml ? (
                  <Box
                    component="iframe"
                    srcDoc={previewHtml}
                    sx={{
                      width: '100%',
                      minHeight: 450,
                      maxHeight: 700,
                      border: 'none',
                      display: 'block',
                      backgroundColor: 'white',
                    }}
                    title="Email Preview"
                    sandbox="allow-same-origin"
                  />
                ) : (
                  <Box display="flex" justifyContent="center" alignItems="center" minHeight={450}>
                    <Typography color="textSecondary">No content to preview</Typography>
                  </Box>
                )}
              </Box>
            </Paper>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setPreviewDialog(false)}>
            Close
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default CampaignsPage;